import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
	Field,
	FormSection,
	getFormValues,
	getFormInitialValues,
	change
} from "redux-form";

import { ReduxControls, ErrorBanner } from "rhombus-react";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";
import TikTokDisclaimer from "social/TikTokDisclaimer";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { formatBytes } from "utils/common";

import { TIKTOK_CTAS } from "social/constants/CTA";
import {
	mapStringToOption,
	dynamicMapStringToOption,
	dynamicNormalizeOptionToString,
	prettifyMessages
} from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { TIKTOK_VIDEO_CONSTRAINTS, getVideoDimensionArrayMessage } from "social/validators/tiktok";
import { getErrorMessagesByAdType } from "selectors/Social2";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";
import { afterEffectsPackage } from "social/AfterEffectsPackageSelector";
import { afterEffectsComposition } from "social/AfterEffectsCompSelector";
import { frameForThumbnail } from "social/FrameForThumbnail";

const CTA_OPTIONS = TIKTOK_CTAS.map(mapStringToOption);

const {
	MAX_LENGTH_LANDING_PAGE,
	MAX_LENGTH_PRIMARY_TEXT,
	MAX_VIDEO_BYTES,
	VIDEO_ACCEPT,
	DURATION,
	MAX_THUMBNAIL_BYTES,
	THUMBNAIL_ACCEPT,
	VIDEO_DIMENSIONS,
} = TIKTOK_VIDEO_CONSTRAINTS;

const TIKTOK_VIDEO_RQUIREMENTS = [
	{
		name: "File Size:",
		description: `${formatBytes(MAX_VIDEO_BYTES)} Maximum`,
	},
	{
		name: "File Types:",
		description: VIDEO_ACCEPT,
	},
	{
		name: "Dimensions:",
		description: getVideoDimensionArrayMessage(VIDEO_DIMENSIONS),
	},
	{
		name: "Duration:",
		description: `Between ${DURATION.min}-${DURATION.max}s`,
	}
];

const TIKTOK_THUMBNAIL_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `<= ${formatBytes(MAX_THUMBNAIL_BYTES)}`,
	},
	{
		name: "File Types:",
		description: THUMBNAIL_ACCEPT,
	}
];

export const TIKTOK_VIDEO_FIELDS = {
	isDynamic,
	assetType,
	imposiumProject,
	imposiumComposition: imposiumCompositionWithoutProjectName,
	afterEffectsPackage,
	afterEffectsComposition,
	video: {
		name: "video",
		label: "Video",
		className: "full-width",
		assetType: ASSET_TYPES.VIDEO,
		component: DynamicAssetSelect,
		defaultDynVarName: "video",
		requirements: TIKTOK_VIDEO_RQUIREMENTS,
		required: true,
		accept: VIDEO_ACCEPT,
		tip: {
			direction: "top",
			description: ".mp4, <= 500MB. 5-60s required (9-15s recommended). 9:16, 1:1, or 16:9 aspect ratio recommended",
		},
	},
	thumbnail: {
		name: "thumbnail",
		label: "Thumbnail",
		className: "full-width",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: "video_thumbnail",
		requirements: TIKTOK_THUMBNAIL_REQUIREMENTS,
		accept: THUMBNAIL_ACCEPT,
		tip: {
			direction: "top",
			description: "Video and thumbnail image must have the same aspect ratio",
		},
	},
	frameForThumbnail,
	name: {
		name: "name",
		label: "Creative Name",
		className: "full-width",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
	},
	primaryText: {
		name: "primaryText",
		label: "Primary Text",
		className: "full-width",
		placeholder: "Tell people what your creative is about",
		tip: {
			description: `Emojis, "{ }" and "#" not allowed`,
			direction: "right",
		},
		component: DynamicInput,
		defaultDynVarName: "copy",
		required: true,
		// showCharCount: true,
		charMax: MAX_LENGTH_PRIMARY_TEXT,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		className: "full-width",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
	},
	disclaimer: {
		name: "disclaimerEnabled",
		component: TikTokDisclaimer,
	},
	cta: {
		name: "cta",
		label: "CTA",
		className: "half-width",
		placeholder: "Enter Call to Action Text",
		component: DynamicSelect,
		defaultDynVarName: "cta",
		options: CTA_OPTIONS,
		normalize: dynamicNormalizeOptionToString,
		format: dynamicMapStringToOption,
		isClearable: true,
		menuPlacement: "top",
		variableNameInputTip: "Edit variable name",
	},
};

function useSynchronizeDynamicState(fieldNames, adType, values){
	const dispatch = useDispatch();

	const synchronizeDynamicState = (alsoValue, value, prevValue, name) => { /*
		The first argument is supposed to be the change event, but Rhombus sends the new value
		as the first and second arguments.
	*/
		fieldNames.forEach((fieldName) => {
			if(fieldName === name){
				return;
			}

			const existingValue = values[fieldName] || { value: null };

			const newValue = {
				...existingValue,
				isDynamic: value.isDynamic,
			};

			if(!newValue.dynVarName){
				/*
					A non-dynamic creative can get created with an empty `dynVarName`, so when we
					edit one, we have to initialize it to the default sometimes.
				*/
				newValue.dynVarName = TIKTOK_VIDEO_FIELDS[fieldName].defaultDynVarName;
			}

			dispatch(change(SOCIAL_WIZARD, `${adType}.${fieldName}`, newValue));
		});
	};

	return synchronizeDynamicState;
}

const synchronizedDynamicFields = ["cta", "landingPage"];

const adType = SOCIAL_AD_TYPES.TIKTOK_VIDEO;

export default function TikTokVideo(props) {
	const {
		navigationActions,
		libraryId,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
		isEditMode,
	} = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const values = formValues[adType] || {};
	const initialValues = useSelector(getFormInitialValues(SOCIAL_WIZARD)) || {};
	const initial = initialValues[adType] || {};

	const { assetType } = values;

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, TIKTOK_VIDEO_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	const synchronizeDynamicState = useSynchronizeDynamicState(synchronizedDynamicFields, adType, values);

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Please review the warnings below" messages={prettyErrorMessages} />
		);
	}

	const fields = Object.values(TIKTOK_VIDEO_FIELDS).map((fieldProps) => {
		const extraProps = {
		};

		if (fieldProps.name === "video" || fieldProps.name === "thumbnail") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = assetType;
		}

		if(fieldProps.name === "frameNumber"){
			extraProps.isEditMode = isEditMode;
		}

		if (fieldProps.name === "cta") {
			extraProps.required = Boolean(values.landingPage?.value);
		}

		if(synchronizedDynamicFields.includes(fieldProps.name)){
			extraProps.onChange = synchronizeDynamicState;
		}

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
		}

		if(fieldProps.name === "landingPage"){
			extraProps.required = Boolean(values.cta?.value);
		}

		if (fieldProps.name === "disclaimerEnabled" && isViewOnly) {
			extraProps.isCreativeViewOnly = true;
			extraProps.hasExistingDisclaimer = !!initial.disclaimerEnabled;
		} else {
			extraProps.disabled = isViewOnly;
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{fields}
				</div>
			</FormSection>
		</div>
	);
}
